import React from 'react';
import Table from '../components/features/Table';
import {GetModuleListAPI} from "../services";
import {useSelector} from "react-redux";
import {Link, useLocation, useParams} from "react-router-dom";
import { useEffect, useState, useRef } from 'react';
import { LanguageLoader } from '../assets/languages/index.js';
import FormatValue from '../components/features/FormatValues';
import AdvancedFilter from '../components/features/AdvancedFilter.jsx';
import FieldContainer from '../components/features/FieldContainer.jsx';
import { DateDBFormat, FixPersianString, GetPastDate } from '../components/utils/index.js';

const ListView = () =>
{
	const preferences = useSelector((state) => state.preferences);	
	const language = LanguageLoader(preferences.language);
	const {module} = useParams();
	const location = useLocation();
	const settings = useSelector((state) => state.settings);
	const [headers, SetHeaders] = useState({});
	const [content, SetContent] = useState([]);
	const [filterString, SetFilterString] = useState('');
	const [advancedFilter, SetAdvancedFilter] = useState(preferences.filters && preferences.filters[module] ? preferences.filters[module] : []);
	const [sort, SetSort] = useState({});
	const debounceTimerRef = useRef(null);
	const skipDebounceRef = useRef(false);
	const definition = settings.definitions.modules[module];
	const fields = definition.fields;

	useEffect(() =>
	{
		GetHeaders();
		GetData();
	// eslint-disable-next-line
	}, [location, advancedFilter]);

	useEffect(() =>
	{
		if(sort && sort.field)
		{
			GetData();
		}
	// eslint-disable-next-line
	}, [sort]);

	useEffect(() =>
	{
		// Skip debounce for initial mount or when explicitly skipped
		if(skipDebounceRef.current) {
			skipDebounceRef.current = false;
			return;
		}
		
		const timer = setTimeout(() => {
			GetData();
		}, 500);
		
		debounceTimerRef.current = timer;
		
		return () => {
			clearTimeout(timer);
		};
	}, [filterString]);

	const GetHeaders = () =>
	{
		let headers = {};
		for(const field in fields)
		{
			let vardef = fields[field];
			if(vardef['list'])
			{
				headers[field] = (typeof vardef.label === 'object') ? vardef.label[preferences.language] : vardef.label;
			}
		}
		SetHeaders(headers);
	}

	const GenerateQueryWithFilter = (filterValue) => //Also handle null filterValue
	{
		let query = '';
		let queries = [];
		/* GENERAL SEARCH */
		if(filterValue !== '' && filterValue !== null)
		{
			let words = [];
			if(filterValue.indexOf(' ') !== -1)
			{
				words = filterValue.split(' ');
			}
			else
			{
				words.push(filterValue);
			}
			if(words.length)
			{
				let stringQuery = [];
				for(const fieldname in fields)
				{
					if(fields[fieldname]["list"])
					{
						// Exclude specific fields from search query for Cases module
						if(module.toLowerCase() === 'cases')
						{
							const excludedFields = ['case_category_c', 'case_origin_c', 'case_severity_c', 'case_reason_c'];
							if(excludedFields.includes(fieldname))
							{
								continue;
							}
						}
						let subQueries = [];
						for(const word of words)
						{
							subQueries.push('(`'+module.toLowerCase()+'`.`'+fieldname+'` LIKE "%'+word+'%" OR `'+module.toLowerCase()+'`.`'+fieldname+'` LIKE "%'+FixPersianString(word)+'%")');
						}
						if(subQueries.length)
						{
							stringQuery.push('('+subQueries.join(' AND ')+')');
						}
					}
				}
				queries.push('('+ stringQuery.join(" OR ") +')');
			}
		}
		/* ADVANCED SEARCH */
		const choices = {
			'equal': '=',
			'in': 'IN',
			'not_in': 'NOT IN',
			'like': 'LIKE',
			'not_equal': '<>',
			'greater': '>',
			'greater_equal': '>=',
			'less': '<',
			'less_equal': '<=',
			'date_range' : 'BETWEEN'
		};
		for (const filter of advancedFilter)
		{
			let dateFilterStart = '';
			if(!choices[filter.operator])
			{
				continue;
			}
			if(filter.operator === 'date_range')
			{
				switch (filter.value)
				{
					case 'day': dateFilterStart = DateDBFormat(GetPastDate(0)); break;
					case 'week': dateFilterStart = DateDBFormat(GetPastDate(7)); break;
					case 'month': dateFilterStart = DateDBFormat(GetPastDate(30)); break;
					case 'quarter': dateFilterStart = DateDBFormat(GetPastDate(90)); break;
					case 'year': dateFilterStart = DateDBFormat(GetPastDate(365)); break;
					default: break;
				}
			}
			const filterField = '`'+module.toLowerCase()+'`.`'+filter['field']+'`';
			const operator = choices[filter.operator];
			let filterValue = filter['value'];
			switch (operator)
			{
				case 'LIKE': filterValue = '"%'+filterValue+'%"'; break;
				case 'IN':
				case 'NOT IN': filterValue = '("'+filterValue.join('", "')+'")'; break;
				case 'BETWEEN': filterValue = `DATE('${dateFilterStart}') AND DATE(NOW())`; break;
				default: filterValue = '"'+filterValue+'"'; break;
			}
			queries.push(filterField+' '+operator+' '+filterValue);
		}
		if(queries.length)
		{
			query = queries.join(' AND ');
		}
		return query;
	}

	const GenerateQuery = () =>
	{
		return GenerateQueryWithFilter(filterString);
	}

	const GetData = async (customFilterString = null) =>
	{
		try
		{
			if (fields)
			{
				SetContent([]);
				let moduleFields = ['id'];
				for(const fieldname in fields)
				{
					let vardef = fields[fieldname];
					if(fields[fieldname]["list"])
					{
						moduleFields.push(fieldname);
						if(vardef.type === 'relate' && vardef.related_to)
						{
							moduleFields.push(vardef.related_to);
						}
					}
				}
				let sortString = '';
				if(sort && sort.field)
				{
					let tableName = definition.table;
					tableName += fields[sort.field]['custom_field'] ? '_cstm' : '';
					sortString = '`'+tableName+'`.`'+sort.field+'`';
					if(sort.order)
					{
						sortString += ' '+sort.order;
					}
				}
				var selectedFields = ['id'];
				for(let field in fields)
				{
					if(fields[field]['list'] === true)
					{
						selectedFields.push(field);
						if(fields[field]['related_to'] && fields[field]['related_to'] !== '')
						{
							selectedFields.push(fields[field]['related_to']);
						}
					}
				}
				const filterToUse = customFilterString !== null ? customFilterString : filterString;
				const queryString = GenerateQueryWithFilter(filterToUse);
				const data = {
					selected_fields: selectedFields,
					offset: 0,
					limit: 500,
					query : queryString,
					order_by: sortString,
				}
				const APIResponse = await GetModuleListAPI(module, data);
				if (APIResponse.length)
				{
					/* PREVENT FILL OUT IF ROUTE HAS BEEN CHANGED */
					if(window.location.pathname.indexOf(module) === -1)
					{
						return;
					}
					SetContent(APIResponse);
				}
				else
				{
					SetContent([]);
				}
			}
		}
		catch (ex) {}
	}

	const GetDataByEnter = (e) => {
		if(e.keyCode === 13)
		{
			if(debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
				debounceTimerRef.current = null;
			}
			GetData();
		}
	}
	if(!definition.access.list)
	{
		return <div className='wrapper flex flex-wrap justify-between items-center'>
			<div>{language.ERROR.NON_ACCESSIBLE}</div>
			<Link to='/' className="btn -mt-1">بازگشت به صفحه اصلی</Link>
		</div>;
	}
	return(
		<>
		<div className='wrapper flex flex-wrap'>
			<div className='title'>
				<h6>{language.FILTER.SEARCH}</h6>
			<button className='btn-primary' type='button' onClick={() => {
				if(filterString) {
					// Cancel any pending debounce timer
					if(debounceTimerRef.current) {
						clearTimeout(debounceTimerRef.current);
						debounceTimerRef.current = null;
					}
					skipDebounceRef.current = true;
					SetFilterString('');
					GetData('');
				} else {
					GetData();
				}
			}}>
				{filterString ? language.AUTHENTICATION.RETURN : language.FILTER.SEARCH}
			</button>
			</div>
			<FieldContainer label={language.FILTER.SEARCH} className={'relative'}>
				<input type="text" value={filterString || ''} onKeyUp={GetDataByEnter} onChange={(e) => SetFilterString(e.target.value)}/>
				{filterString.length ? <i className='fa fa-eraser absolute left-2 p-4 cursor-pointer hover:text-primary' onClick={() => {
					if(debounceTimerRef.current) {
						clearTimeout(debounceTimerRef.current);
						debounceTimerRef.current = null;
					}
					skipDebounceRef.current = true;
					SetFilterString('');
					GetData('');
				}}></i> : null}
			</FieldContainer>
			<AdvancedFilter fields={fields} callback={(filters) => SetAdvancedFilter(filters)}/>
		</div>
		<div className="wrapper overflow-auto flex flex-wrap">
			<Table title={definition.label[preferences.language]} counter={true} header={headers} SortCallback={SetSort} itemsPerPage={20} className={'responsive'}>
				{content.map((row, index) =>
				{
					return <tr key={index}>
						{Object.keys(row).map((field, colIndex) =>
						{
							let vardef = fields[field];
							if(vardef && vardef["list"] && Object.keys(headers).indexOf(field) !== -1)
							{
								let fieldValue = row[field];
								let value;
								
								// Check if value exists and is not empty
								const hasValue = fieldValue !== undefined && fieldValue !== null && 
									(Array.isArray(fieldValue) ? fieldValue.length > 0 : 
									 (typeof fieldValue === 'string' ? fieldValue.length > 0 : 
									  fieldValue !== ''));
								
								if(hasValue)
								{
									// For arrays (multienum), pass directly to FormatValue
									// For other types, convert to string
									const valueToFormat = Array.isArray(fieldValue) ? fieldValue : String(fieldValue);
									value = <FormatValue value={valueToFormat} vardef={vardef} format={true} related_to={(vardef['related_to'] ? row[vardef['related_to']] : null)}/>;
								}
								else
								{
									value = '---';
								}
								
								if((colIndex === 0 || colIndex === 1 || window.innerWidth < 640) && row['id'] && definition.access.view)
								{
									value = <Link to={'/detail/'+module+'/'+row['id']}>{value}{vardef.type === 'currency' && row.currency_symbol ? ' '+row.currency_symbol : null}</Link>
								}
								return <td key={colIndex} data-label={headers[field]}>{value}{vardef.type === 'currency' && row.currency_symbol ? ' '+row.currency_symbol : null}</td>
							}
							return null;
						})}
					</tr>
				})}
			</Table>
		</div>
		</>
	);
}

export default ListView;